import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import Cookies from 'js-cookie';
import { Hand } from 'lucide-react';

import MiddleContentComponent from '../../components/DiscoveryComponent/MiddleContentComponent';
import RightContentComponent from '../../components/DiscoveryComponent/RightContentComponent';
import ListFilter from '../../components/DiscoveryComponent/MiddleContentComponent/components/list-filter.component';
import Footer from '../../components/Footer';
import { useGetListArticleByFilter } from '../../hooks/useGetListArticleByFilter';
import WelcomeModal2 from '../../components/WelcomeModal2';
import DataLayer from '../../utils/data-layer';
import SEO from '../../components/SEO';
import { generateSEOMetadata } from '../../utils/seo';
import LeftContentComponent from '../../components/DiscoveryComponent/LeftContentComponent';

const DiscoveryPage = () => {
  const token = Cookies.get('token');
  const [searchParams, setSearchParams] = useSearchParams();
  const [isModalOpen, setIsModalOpen] = useState(() => {
    // Check if user has already completed the welcome modal
    const hasCompletedWelcome = localStorage.getItem('twygs_welcome_completed');
    return !hasCompletedWelcome; // Only show if they haven't completed it
  });

  const initialType = searchParams.get('tab') || (token ? 'forYou' : 'recent');
  const [selected, setSelected] = useState({ type: initialType, topic: [] });

  const { loading, listArticle, setPagination } = useGetListArticleByFilter({
    type: selected.type,
    topic: selected.topic
  });

  useEffect(() => {
    const newType = searchParams.get('tab') || (token ? 'forYou' : 'recent');
    if (selected.type !== newType) {
      setSelected({ type: newType, topic: [] });
    }
  }, [searchParams, token]);

  // Track category view when selected type changes
  useEffect(() => {
    if (selected.type) {
      DataLayer.trackViewCategory({ categoryName: selected.type });
    }
  }, [selected.type]);

  // Generate SEO metadata for homepage
  const seoMetadata = generateSEOMetadata.homepage();

  return (
    <div className="max-w-[1920px] mx-auto min-h-full">
      <SEO {...seoMetadata} />
      <WelcomeModal2
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          // Save to localStorage that user has completed/dismissed the welcome modal
          localStorage.setItem('twygs_welcome_completed', 'true');
        }}
      />

      {/* Mobile: fixed left component */}
      <div className="block lg:hidden">
        <LeftContentComponent isFixed />
      </div>

      {/* 3-column fluid layout: left fixed, middle fluid, right fixed */}
      <div className="flex w-full -mt-14 md:mt-0">
        {/* Left sidebar - fixed width */}
        <div className="hidden lg:block flex-shrink-0 mr-2">
          <LeftContentComponent />
        </div>

        {/* Middle content - fluid, grows to fill available space */}
        <div className="flex-1 min-w-0 px-2 md:px-4">
          <div className="max-w-5xl mx-auto">
            <div className="relative z-10 w-full scale-90 md:scale-100 origin-top-left">
              <ListFilter
                selected={selected}
                setSelected={setSelected}
                setPagination={setPagination}
                setSearchParams={setSearchParams}
                isEmpty={!(listArticle ?? []).length}
              />
            </div>

            <div className="w-full pt-3 md:pt-10">
              <MiddleContentComponent
                selected={selected}
                loading={loading}
                listArticle={listArticle ?? []}
              />
            </div>
          </div>
        </div>

        {/* Right sidebar - fixed width, hidden below xl (1280px) */}
        <div className="hidden xl:block w-[320px] flex-shrink-0 pr-4 pt-3 md:pt-5 ">
          <RightContentComponent />
          <Footer />
        </div>
      </div>

      {/* Welcome Guide FAB - Only show when welcome modal is closed */}
      {!isModalOpen && (
        <button
          onClick={() => {
            setIsModalOpen(true);
            // // Remove the localStorage flag so modal can be shown again
            // localStorage.removeItem('twygs_welcome_completed');
          }}
          className="fixed bottom-6 right-6 bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white p-3 rounded-full shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-300 z-[60] group"
          style={{
            background: 'linear-gradient(135deg, #4CAF50, #45a049)'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = 'linear-gradient(135deg, #45a049, #3d8b40)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = 'linear-gradient(135deg, #4CAF50, #45a049)';
          }}
          aria-label="Reopen Welcome Guide"
        >
          <Hand className="w-5 h-5 group-hover:rotate-12 transition-transform duration-300" />

          <div className="absolute bottom-full right-0 mb-2 px-3 py-2 bg-gray-900 text-white text-sm rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-300 whitespace-nowrap pointer-events-none z-[60]">
            Welcome Guide
            <div className="absolute top-full right-4 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-900"></div>
          </div>
        </button>
      )}
    </div>
  );
};

export default DiscoveryPage;
